import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { trackLandingCta } from "../../hooks/useLandingAnalytics";
import { scrollToSection } from "./scrollToSection";

type Step = { title: string; body: string };

// Entrance motion is CSS-only (`animate-fade-in-up`): it runs on the
// compositor thread, so a busy main thread (cold JS execution on phones)
// can't hold the content invisible — which framer-motion's rAF-driven
// animations did for seconds on iPhone Safari (see SESSIONS 2026-06-12).
export function Hero() {
  const { t } = useTranslation();
  const proof = t("landing.hero.proof", { returnObjects: true }) as string[];
  const steps = t("landing.how.steps", { returnObjects: true }) as Step[];

  return (
    <section id="hero" className="relative overflow-hidden bg-white pb-14 pt-28 dark:bg-slate-950 sm:pb-20 sm:pt-36">
      {/* Layered accent glows + masked grid — bold but calm */}
      <div className="pointer-events-none absolute left-1/3 top-0 h-[30rem] w-[50rem] -translate-x-1/2 -translate-y-1/3 rounded-full glow [--glow:theme(colors.blue.400/15%)] dark:[--glow:theme(colors.blue.600/25%)]" />
      <div className="pointer-events-none absolute right-[5%] top-40 h-72 w-72 rounded-full glow [--glow:theme(colors.violet.400/10%)] dark:[--glow:theme(colors.violet.600/15%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[30rem] bg-grid-slate bg-grid-md opacity-40 [mask-image:linear-gradient(to_bottom,black,transparent)] dark:opacity-25" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
          {/* Left — message + CTAs */}
          <div className="animate-fade-in-up text-center lg:text-left">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3.5 py-1.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200 dark:bg-white/5 dark:text-blue-200 dark:ring-white/10">
              <Sparkles size={13} className="text-blue-500 dark:text-blue-300" />
              {t("landing.hero.badge")}
            </div>

            <h1 className="text-[2.6rem] font-bold leading-[1.05] tracking-tight text-slate-900 dark:text-white sm:text-6xl">
              {t("landing.hero.headlineLine1")}
              <br />
              <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-violet-600 bg-clip-text text-transparent dark:from-blue-400 dark:via-indigo-400 dark:to-violet-400">
                {t("landing.hero.headlineAccent")}
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-600 dark:text-slate-400 sm:text-lg lg:mx-0">
              {t("landing.hero.subheadline")}
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              <Link
                to="/register?src=landing"
                onClick={() => trackLandingCta("hero_primary", "hero")}
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:shadow-blue-500/50 hover:from-blue-400 hover:to-indigo-500"
              >
                {t("landing.hero.ctaPrimary")}
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#capabilities"
                onClick={(e) => {
                  e.preventDefault();
                  trackLandingCta("hero_secondary", "hero");
                  scrollToSection("#capabilities");
                }}
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-300 transition-colors hover:bg-slate-50 hover:text-slate-900 dark:text-slate-200 dark:ring-white/15 dark:hover:bg-white/5 dark:hover:text-white"
              >
                {t("landing.hero.ctaSecondary")}
              </a>
            </div>

            <p className="mt-6 text-xs text-slate-500">{t("landing.hero.earlyAccessNote")}</p>
          </div>

          {/* Right — how it works, inside the hero */}
          <div
            className="animate-fade-in-up relative mx-auto w-full max-w-md lg:mx-0 lg:ml-auto"
            style={{ animationDelay: "0.15s" }}
          >
            <div className="pointer-events-none absolute -inset-8 rounded-full glow [--glow:color-mix(in_srgb,var(--accent-400)_10%,transparent)] dark:[--glow:color-mix(in_srgb,var(--accent-500)_10%,transparent)]" />
            <div
              id="how-it-works"
              className="relative rounded-3xl bg-white/95 p-6 ring-1 ring-slate-200 shadow-xl shadow-slate-200/60 md:bg-white/90 md:backdrop-blur dark:bg-white/[0.03] dark:ring-white/10 dark:shadow-2xl dark:shadow-black/40 sm:p-7"
            >
              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10">
                {t("landing.how.tag")}
              </span>
              <p className="mt-3 text-lg font-bold leading-snug text-slate-900 dark:text-white">
                {t("landing.how.title")}
              </p>

              <ol className="mt-6">
                {steps.map((step, i) => (
                  <li key={step.title} className="relative flex gap-4 pb-6 last:pb-0">
                    {i < steps.length - 1 && (
                      <span
                        className="absolute bottom-0 left-[15px] top-9 w-px bg-slate-200 dark:bg-white/10"
                        aria-hidden
                      />
                    )}
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-xs font-bold text-white shadow-lg shadow-blue-500/30 ring-4 ring-white dark:ring-slate-950/60">
                      {i + 1}
                    </span>
                    <div className="min-w-0 pt-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{step.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                        {step.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Trust strip — honest product facts */}
        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-slate-200/80 pt-7 dark:border-white/5">
          {proof.map((item) => (
            <span key={item} className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <Check size={14} className="text-emerald-600 dark:text-emerald-400" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
